
/**
 *
 * defines a class to handle a transaction
 *
 * - initiates the trade
 * - watches for notifications
 * - sends state to the UI for display
 * - manages the state machine for all this
 *
 */

import {Contract, ethers, utils} from 'ethers';
import {store} from '../store/Store';
import {setAllowance} from '../lib/Wallet';
import {reloadHistoryFromApi} from "../store/OrderHistoryManager";
import type {Order} from "../types";
import {useOrderHistoryStore} from "../store/OrderHistory";
import {v4 as uuidv4} from 'uuid';
import {OrderRequest, sendOrderRequest} from "./RestApiClient";
import BigNumber from "bignumber.js";
import {addLeveragePosition} from "./LeveragePositions";


type T_TRANSACTION_STATES = 'NEW' | 'PENDING' | 'SETTLED' | 'CANCELLED';

const NATIVE_ETH = "0x0000000000000000000000000000000000000000";

export class Transaction {
    sellToken: string;
    buyToken: string;
    quantityToSell: BigNumber;
    limitAmountToGet: BigNumber;
    state: T_TRANSACTION_STATES;

    constructor(sellToken: string, buyToken: string, quantityToSell: BigNumber, limitAmountToGet?: BigNumber) {
        this.sellToken = sellToken;
        this.buyToken = buyToken;
        this.quantityToSell = quantityToSell;
        this.limitAmountToGet = limitAmountToGet || new BigNumber(0);
        this.state = 'NEW';
    }

    async send() {
        const currentState = store.web3Onboard.state.get()
        const wallets = currentState.wallets;
        const chainInfo = store.getChainInfo();
        if (wallets.length > 0 && chainInfo) {
            const wallet = wallets[0];
            const walletProvider = store.getWalletProvider(wallet);
            try {
                const sc: Contract = new Contract(chainInfo.smartContractAddress, chainInfo.abi, walletProvider.getUncheckedSigner());
                const amount: ethers.BigNumber = utils.parseUnits(this.quantityToSell.toFixed(store.tokenDPs[this.sellToken]), store.tokenDPs[this.sellToken]);
                const truncatedLimitStr = this.limitAmountToGet.toFixed(store.tokenDPs[this.buyToken]);
                const truncatedLimit = new BigNumber(truncatedLimitStr);
                const limit: ethers.BigNumber = utils.parseUnits(truncatedLimitStr, store.tokenDPs[this.buyToken]);

                const timestampOfSubmission: number = (new Date()).getTime();
                console.log('>> will place order @', timestampOfSubmission, store.tokenDPs[this.sellToken], this.sellToken, this.buyToken, this.quantityToSell, '=',
                    amount, truncatedLimit, '=', limit);

                const result = store.tokenAddresses[this.sellToken] === NATIVE_ETH
                    ? await sc.placeOrder(store.tokenAddresses[this.sellToken], store.tokenAddresses[this.buyToken], amount, limit, {value: amount})
                    : await sc.placeOrder(store.tokenAddresses[this.sellToken], store.tokenAddresses[this.buyToken], amount, limit);

                const orderStore = useOrderHistoryStore();

                const pendingOrder: Order = {
                    buyingAsset: store.tokenToAssetMap[this.buyToken.toLowerCase()],
                    sellingAsset: store.tokenToAssetMap[this.sellToken.toLowerCase()],
                    limitAmount: truncatedLimit,
                    chainOrderSource: {
                        orderSource: chainInfo.label,
                        smartContractAddress: chainInfo.smartContractAddress
                    },
                    quantityBought: new BigNumber(0),
                    quantitySold: this.quantityToSell,
                    feePaid: new BigNumber(0),
                    placeTransactionId: result.hash,
                    status: "PENDING",
                    timestamp: +Date.now(),
                    refundable: false,
                    sellingTokenAddress: store.tokenAddresses[this.sellToken],
                    buyingTokenAddress: store.tokenAddresses[this.buyToken]
                }

                store.lastPlacedOrder = pendingOrder;
                orderStore.upsertOrder(pendingOrder);

                result.wait(1).then(() => {
                    reloadHistoryFromApi()

                    console.log('order result', timestampOfSubmission, result);
                });
            }
            catch (e) {
                console.log("Caught exception will check allowance");

                console.log('>> will check allowance', this.sellToken, this.quantityToSell);
                const result: boolean = await setAllowance(this.sellToken, +(this.quantityToSell), chainInfo.smartContractAddress);
                if (result) {
                    console.log("Updated allowance")
                }
                else {
                    console.error('Allowance checking didnt work was a different TX error', (e as any));
                }
            }
        }
        else {
            console.log('no transaction; no wallets/chains', wallets.length, chainInfo);
        }
    }

    async sendDtwOrder() {
        const currentState = store.web3Onboard.state.get()
        const wallets = currentState.wallets;
        console.log("Backend info", store.config);
        const chainInfo = store.getChainInfo();
        console.log("Chain info", chainInfo);
        if (wallets.length > 0 && chainInfo) {
            const wallet = wallets[0];
            const walletProvider = store.getWalletProvider(wallet);
            console.log('Sending transaction with wallet ', wallet, " provider", walletProvider);
            try {
                const amount: ethers.BigNumber = utils.parseUnits(this.quantityToSell.toFixed(store.tokenDPs[this.sellToken]), store.tokenDPs[this.sellToken]);
                const truncatedLimitStr = this.limitAmountToGet.toFixed(store.tokenDPs[this.buyToken]);
                const limit: ethers.BigNumber = utils.parseUnits(truncatedLimitStr, store.tokenDPs[this.buyToken]);

                const signer = walletProvider.getUncheckedSigner();

                const chainId = chainInfo.chainId;
                const dtwAddress = chainInfo.dtwContractAddress;

                let sellingTokenAddress = store.tokenAddresses[this.sellToken];
                if (sellingTokenAddress == '0x0000000000000000000000000000000000000000') {
                    sellingTokenAddress = chainInfo.wethContractAddress;
                }
                let buyingTokenAddress = store.tokenAddresses[this.buyToken];
                if (buyingTokenAddress == '0x0000000000000000000000000000000000000000') {
                    buyingTokenAddress = chainInfo.wethContractAddress;
                }

                const deadline = (+Date.now()) + 3600; // TODO: Put an actual deadline

                const domain = {
                    name: "DTW",
                    version: "1",
                    chainId: Number(chainId),
                    verifyingContract: dtwAddress,
                }
                const types = {
                    Order: [
                        { name: "user", type: "address" },
                        { name: "buyingToken", type: "address" },
                        { name: "limitAmountToBuy", type: "uint256" },
                        { name: "sellingToken", type: "address" },
                        { name: "amountToSell", type: "uint256" },
                        { name: "deadline", type: "uint256" }
                    ]
                }
                const values = {
                    user: wallet.accounts[0].address,
                    buyingToken: buyingTokenAddress,
                    limitAmountToBuy: limit,
                    sellingToken: sellingTokenAddress,
                    amountToSell: amount,
                    deadline: deadline,
                }

                const signature = await signer._signTypedData(domain, types, values);
                const sellingAsset = sellingTokenAddress;
                const buyingAsset = buyingTokenAddress;
                const orderRequest: OrderRequest = {
                    instructionId: uuidv4(),
                    orderSource: chainInfo.label,
                    sellingAsset,
                    buyingAsset,
                    amountToSell: this.quantityToSell.toString(),
                    limitAmountToReceive: truncatedLimitStr,
                    deadline: deadline.toString(),
                    signature: signature
                }
                console.log("Order orderRequest", orderRequest);

                const response = await sendOrderRequest(orderRequest);
                const responseObject = await response.json();

                console.log("Order response", responseObject);

            }
            catch (e) {
                console.log("Caught exception will check allowance");

                console.log('>> will check allowance', this.sellToken, this.quantityToSell);
                const result: boolean = await setAllowance(this.sellToken, +(this.quantityToSell), chainInfo.dtwContractAddress);
                if (result) {
                    console.log("Updated allowance")
                }
                else {
                    console.error('Allowance checking didnt work was a different TX error', (e as any));
                }
            }
        }
        else {
            console.log('no transaction; no wallets/chains', wallets.length, chainInfo);
        }
    }

}


export function sendTransaction(isDtw: boolean) {

    if (store.leverage > 1) {
        let instSymbol: string = "lev_" + store.frontToken + '/' + store.backToken;
        let { id } = store.instrumentPairs[instSymbol];
        addLeveragePosition(id, new BigNumber(store.sellAmount), new BigNumber(store.leverage))
            .then(() => console.log('Position added successfully'))
            .catch(err => console.error('Error adding position:', err));
    }
    else {
        let transaction: Transaction = new Transaction(
            store.frontToken,
            store.backToken,
            new BigNumber(store.sellAmount),
            store.limitAmount ? new BigNumber(store.limitAmount) : new BigNumber(0)
        );
        if (isDtw) {
            transaction.sendDtwOrder();
        }
        else {
            transaction.send();
        }
    }
}
